import type {
  TicketImageValue,
  TicketProductApiItem,
  TicketProductOption,
  TicketProductPayload,
} from "@/api/types/ticket";
import { findMissingRequiredFields, type MissingRequiredField } from "@/utils/formRequired";

export type TicketExpirationUnit = "years" | "months" | "target_date";

export interface TicketProductDraft {
  name: string;
  bundle: string | number;
  price: string | number;
  membershipPrice: string | number;
  stock: string | number;
  noLimit: boolean;
  canRefundEach: boolean;
  unit: TicketExpirationUnit;
  period: string | number;
  targetDate: string;
  descript: string;
  usingInfo: string;
  thumbnail: TicketImageValue;
  detailImg: TicketImageValue;
  state?: string;
}

interface TicketProductFieldIds {
  name?: string;
  bundle?: string;
  price?: string;
  descript?: string;
  usingInfo?: string;
}

export const normalizeTicketInteger = (value: string | number, fallback = 0): number => {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const buildTicketExpirationPeriod = (
  draft: TicketProductDraft,
): TicketProductPayload["expiration_period"] => {
  if (draft.unit === "target_date") {
    return { target_date: draft.targetDate };
  }

  const normalizedPeriod = Math.max(normalizeTicketInteger(draft.period, 1), 1);

  if (draft.unit === "months") {
    return { months: normalizedPeriod };
  }

  return { years: normalizedPeriod };
};

export const buildTicketProductPayload = (
  draft: TicketProductDraft,
  overrides?: Partial<Pick<TicketProductPayload, "state" | "priority">>,
): TicketProductPayload => {
  const normalizedPrice = normalizeTicketInteger(draft.price, 0);
  const normalizedBundleCount = Math.max(normalizeTicketInteger(draft.bundle, 1), 1);

  return {
    name: draft.name,
    price: normalizedPrice,
    membership_price: draft.membershipPrice
      ? normalizeTicketInteger(draft.membershipPrice, normalizedPrice)
      : normalizedPrice,
    bundle_count: normalizedBundleCount,
    can_refund_each: normalizedBundleCount > 1 ? draft.canRefundEach : false,
    stock_count: draft.noLimit ? -1 : normalizeTicketInteger(draft.stock, 0),
    descript: draft.descript,
    usingInfo: draft.usingInfo,
    expiration_period: buildTicketExpirationPeriod(draft),
    state: overrides?.state ?? draft.state ?? "노출중",
    thumbnail: draft.thumbnail,
    image: draft.detailImg,
    ...(typeof overrides?.priority === "number" ? { priority: overrides.priority } : {}),
  };
};

export const findMissingTicketProductFields = (
  draft: TicketProductDraft,
  fieldIds: TicketProductFieldIds = {},
): MissingRequiredField[] => {
  return findMissingRequiredFields([
    { label: "상품명", value: draft.name, elementId: fieldIds.name ?? "productName" },
    { label: "티켓수", value: draft.bundle, elementId: fieldIds.bundle ?? "bundle" },
    { label: "가격", value: draft.price, elementId: fieldIds.price ?? "price" },
    { label: "상세정보", value: draft.descript, elementId: fieldIds.descript ?? "descriptEditor" },
    { label: "이용안내", value: draft.usingInfo, elementId: fieldIds.usingInfo ?? "usingInfoEditor" },
  ]);
};

export const toTicketProductOption = (
  item: Pick<TicketProductApiItem, "id" | "name" | "bundle_count" | "can_refund_each">,
): TicketProductOption => ({
  value: item.id,
  label: item.name,
  bundle_count: item.bundle_count,
  can_refund_each: item.can_refund_each,
});

export const mergeTicketProductOptions = (
  current: TicketProductOption[],
  incoming: TicketProductOption[],
): TicketProductOption[] => {
  const optionMap = new Map<number, TicketProductOption>();

  [...current, ...incoming].forEach((option) => {
    optionMap.set(option.value, option);
  });

  return Array.from(optionMap.values());
};

export const isAmazingPassTicket = (
  ticket: Partial<Pick<TicketProductApiItem, "state">>,
): boolean => {
  return ticket.state === "어메이징패스";
};